const SRC = 'https://pay.google.com/gp/p/js/pay.js';

let loading: Promise<void> | null = null;

/**
 * Loads Google's pay.js, once per page however many buttons ask for it.
 *
 * headless put this in `_app`'s `<Head>` and every legacy jshop template that shows the
 * button carries its own `<script src>`. Neither works for a component that is supposed
 * to drop into any page: the first makes it the layout's problem and the second makes it
 * the template's, and both mean a third-party script in the critical path of every
 * visitor, the overwhelming majority of whom will never use the button.
 *
 * Appended imperatively rather than rendered as JSX for the reason ProductReviewsPanel
 * gives: React DOM builds script elements in a way that marks them parser-inserted, so a
 * `<script src>` written as JSX lands in the DOM looking right and is never fetched.
 *
 * The promise is memoized rather than the element looked up each time, so two buttons
 * mounting together share one load and both wait for the same `onload` — checking for an
 * existing tag would let the second one continue while the first was still in flight.
 */
export default function loadGooglePayScript(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Google Pay needs a document'));
  }

  if (loading) {
    return loading;
  }

  loading = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`);

    if (existing) {
      // A page that still carries the tag itself. It may already have run, and a load
      // listener on a script that has finished never fires, so ask the SDK first.
      if (window.google?.payments?.api) {
        resolve();
        return;
      }

      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('pay.js failed to load')));
      return;
    }

    const script = document.createElement('script');

    script.src = SRC;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('pay.js failed to load')));

    document.body.appendChild(script);
  });

  // Only a *resolved* load is remembered. Memoizing a rejection would make one dropped
  // request permanent for the page, and the second button would never retry.
  loading = loading.catch((error) => {
    loading = null;

    throw error;
  });

  return loading;
}
